import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Database, 
  Link as LinkIcon, 
  CreditCard, 
  Star, 
  Palette, 
  Package, 
  Settings, 
  LogOut,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  Users,
  FileText,
  Ticket
} from 'lucide-react';

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const menuItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin/dashboard',
    },
    {
      label: 'Master Data',
      icon: Database,
      nested: true,
      items: [
        { label: 'Tags', path: '/admin/tags' },
        { label: 'Categories', path: '/admin/categories' },
        { label: 'Materials', path: '/admin/materials' },
        { label: 'Card Types', path: '/admin/card-types' },
        { label: 'Card Categories (Digital Cards)', path: '/admin/card-categories' },
        { label: 'Colours', path: '/admin/colours' },
        { label: 'Countries', path: '/admin/countries' },
      ],
      isOpen: masterDataOpen,
      setIsOpen: setMasterDataOpen,
    },
    {
      label: 'Dynamic Links',
      icon: LinkIcon,
      path: '/admin/dynamic-links',
    },
    {
      label: 'Coupons',
      icon: Ticket,
      path: '/admin/coupons',
    },
    // Hidden menu items - kept for future use
    // {
    //   label: 'Business Cards',
    //   icon: CreditCard,
    //   path: '/admin/business-cards',
    // },
    // {
    //   label: 'Review Cards',
    //   icon: CreditCard,
    //   path: '/admin/review-cards-new',
    // },
    {
      label: 'Review Standees',
      icon: Star,
      path: '/admin/review-standees',
    },
    // {
    //   label: 'Artists',
    //   icon: Palette,
    //   path: '/admin/artists',
    // },
    // {
    //   label: 'Requests',
    //   icon: CreditCard,
    //   path: '/admin/card-requests',
    // },
    {
      label: 'Orders',
      icon: Package,
      path: '/admin/orders',
    },
    {
      label: 'Users',
      icon: Users,
      path: '/admin/users',
    },
    {
      label: 'Activity History',
      icon: FileText,
      path: '/admin/login-logs',
    },
    {
      label: 'Settings',
      icon: Settings,
      nested: true,
      items: [
        { label: 'Shipping Charge Management', path: '/admin/shipping-charges' },
      ],
      isOpen: settingsOpen,
      setIsOpen: setSettingsOpen,
    },
  ];

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold" style={{ color: '#7D64FA' }}>Smart Connections</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mt-1">Admin Panel</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4">
        <ul className="space-y-1">
          {menuItems.map((item, index) => (
            <li key={index}>
              {item.nested ? (
                // Nested menu item
                <div>
                  <button
                    onClick={() => item.setIsOpen(!item.isOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    {item.isOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  {item.isOpen && (
                    <ul className="ml-8 mt-1 space-y-1">
                      {item.items.map((subItem, subIndex) => (
                        <li key={subIndex}>
                          <button
                            onClick={() => navigate(subItem.path)}
                            className={`w-full text-left px-4 py-2 text-sm rounded-lg transition-colors ${
                              isActive(subItem.path)
                                ? 'text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            style={isActive(subItem.path) ? { backgroundColor: '#7D64FA' } : {}}
                          >
                            {subItem.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                // Regular menu item
                <button
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.path)
                      ? 'text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  style={isActive(item.path) ? { backgroundColor: '#7D64FA' } : {}}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* User Info & Logout */}
      <div className="p-4 border-t border-gray-200">
        <div className="mb-3">
          <p className="text-sm font-semibold truncate" style={{ color: '#7D64FA' }}>
            {localStorage.getItem('admin_email')}
          </p>
          <p className="text-xs text-gray-500">Administrator</p>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 bg-white border-r border-gray-200">
        <SidebarContent />
      </aside>

      {/* Sidebar - Mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:hidden ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <SidebarContent />
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-600 hover:text-gray-900"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex-1 lg:flex lg:justify-end">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 font-medium hover:opacity-80"
              style={{ color: '#7D64FA' }}
            >
              View Website →
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
